import { useEffect, useState } from 'react'
import { supabase } from '../../services/supabaseClient'
import { useAppSelector } from '../../store/hooks'
import AssignmentCard from '../base/AssignmentCard'
import styles from './TeamViewer.module.css'

interface TeamMember {
  id: string
  name: string
  role?: string
  completed: boolean
  data?: Record<string, unknown>
}

interface TeamViewerProps {
  toolId: string
}

export default function TeamViewer({ toolId }: TeamViewerProps) {
  const profile = useAppSelector((s) => s.auth.profile)
  const userId = useAppSelector((s) => s.auth.user?.id)
  const [members, setMembers] = useState<TeamMember[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!profile?.team_id || !userId) {
      setLoading(false)
      return
    }

    async function loadTeamData() {
      setLoading(true)

      // Get team profiles
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, display_name, role')
        .eq('team_id', profile!.team_id!)

      if (!profiles) { setLoading(false); return }

      // Get team responses for this tool
      const { data: responses } = await supabase
        .from('responses')
        .select('user_id, data')
        .eq('tool_id', toolId)

      const responseMap = new Map<string, Record<string, unknown>>()
      responses?.forEach((r) => {
        responseMap.set(r.user_id, r.data as Record<string, unknown>)
      })

      const teamMembers: TeamMember[] = profiles
        .filter((p) => p.id !== userId) // skip self
        .map((p) => ({
          id: p.id,
          name: p.display_name ?? 'Unknown',
          role: p.role ?? undefined,
          completed: responseMap.has(p.id),
          data: responseMap.get(p.id),
        }))

      setMembers(teamMembers)
      setLoading(false)
    }

    loadTeamData()
  }, [profile, userId, toolId])

  if (!profile?.team_id) {
    return (
      <div className={styles.empty} data-testid="team-viewer-no-team">
        Join a team to see teammates' responses
      </div>
    )
  }

  if (loading) {
    return (
      <div className={styles.empty} data-testid="team-viewer-loading">
        Loading team responses...
      </div>
    )
  }

  if (members.length === 0) {
    return (
      <div className={styles.empty} data-testid="team-viewer-empty">
        No other team members found
      </div>
    )
  }

  return (
    <div className={styles.grid} data-testid="team-viewer">
      {members.map((m) => (
        <AssignmentCard
          key={m.id}
          name={m.name}
          role={m.role}
          completed={m.completed}
          data={m.data}
        />
      ))}
    </div>
  )
}
